import { useQuery } from "@tanstack/react-query";

import { experienceApi } from "@/lib/api/earnings";
import { ApiRequestError } from "@/lib/api/client";
import { qk } from "@/lib/query-client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/ui/EmptyState";
import { Icon } from "@/components/layout/Icon";
import { formatDate } from "@/lib/utils/format";
import { LIVE_REFRESH_MS } from "./liveCopy";
import { CountUp } from "./MyEarningsCard";
import { useAmountPrivacy } from "./useAmountPrivacy";

/** What the employee has earned and has not yet been paid through payroll.
 * A calculated view, not a stored ledger: the server works it out from the
 * confirmed attendance and the payroll runs every time, so it cannot drift
 * from them. While the employee is checked in it follows the time worked. */
export function WalletCard() {
  const { money } = useAmountPrivacy();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: qk.earnings.wallet,
    queryFn: experienceApi.wallet,
    gcTime: 5 * 60 * 1000,
    refetchInterval: (q) => (q.state.data?.live_active ? LIVE_REFRESH_MS : false),
  });

  if (isLoading) {
    return (
      <Card aria-busy="true" data-testid="wallet-loading">
        <CardBody className="space-y-3">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-10 w-44" />
          <Skeleton className="h-4 w-full" />
        </CardBody>
      </Card>
    );
  }
  if (error) {
    if (error instanceof ApiRequestError && (error.status === 403 || error.status === 404)) return null;
    return (
      <Card>
        <ErrorState title="Couldn't load your wallet" onRetry={() => refetch()} className="py-8" />
      </Card>
    );
  }
  if (!data || !data.enabled) return null;

  const empty = !data.current && !data.previous;

  return (
    <Card data-testid="wallet-card" className="dark:border-slate-700 dark:bg-slate-900">
      <CardHeader>
        <CardTitle className="dark:text-slate-100">My Wallet</CardTitle>
        {data.live_active && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300" data-testid="wallet-live">
            <Icon name="trending-up" className="h-3.5 w-3.5" />
            Earning now
          </span>
        )}
      </CardHeader>
      <CardBody className="space-y-4">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Earned, not yet paid</p>
          <p className="mt-0.5 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums dark:text-white" data-testid="wallet-balance">
            <CountUp value={data.balance} />
          </p>
        </div>

        {empty ? (
          <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="wallet-empty">
            Nothing earned yet. Your earnings are added here as each day is confirmed.
          </p>
        ) : (
          <ul className="space-y-2 text-sm" data-testid="wallet-parts">
            {data.current && (
              <li className="flex items-start justify-between gap-3">
                <span className="text-slate-600 dark:text-slate-300">
                  {data.current.period_label}
                  {data.current.live > 0 && (
                    <span className="block text-xs text-emerald-700 dark:text-emerald-400">includes {money(data.current.live)} for today so far</span>
                  )}
                </span>
                <span className="font-medium tabular-nums text-slate-900 dark:text-slate-100">{money(data.current.amount)}</span>
              </li>
            )}
            {data.previous && (
              <li className="flex items-start justify-between gap-3">
                <span className="text-slate-600 dark:text-slate-300">
                  {data.previous.period_label}
                  <span className="block text-xs text-slate-500 dark:text-slate-400">
                    {data.previous.approved ? "approved by payroll, awaiting payment" : "estimate, payroll not paid yet"}
                  </span>
                </span>
                <span className="font-medium tabular-nums text-slate-900 dark:text-slate-100">{money(data.previous.amount)}</span>
              </li>
            )}
          </ul>
        )}

        {data.payday_configured && data.next_payday && (
          <p className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300" data-testid="wallet-payday">
            <Icon name="calendar" className="h-3.5 w-3.5" />
            Next payday {formatDate(data.next_payday)}
          </p>
        )}

        <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
          Gross earnings you have not been paid yet. Today's amount follows the time you work and is confirmed when you check out. PF, ESI and tax are taken off when payroll pays you.
        </p>
      </CardBody>
    </Card>
  );
}
